import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';

interface SuperAdminRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component specifically for SuperAdmin access
 * Checks if the logged-in user has SuperAdmin role and redirects appropriately
 */
export function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const location = useLocation();
  const { user, isAuthenticated, isLoading } = useAuthStore();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for SuperAdmin role (matching backend role name)
  // The backend uses 'SuperAdmin' in JWT claims, while frontend may use 'platform_admin'
  const isSuperAdmin = user.role === 'platform_admin' || user.role === 'SuperAdmin';

  // Redirect to unauthorized page if not SuperAdmin
  if (!isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}